import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ReactTable from "@components/ReactTable";
import { useNavigate, useParams } from "react-router-dom";
import { EditIcon, TrashIcon } from "@components/icons";
import { useMutation, useQueryClient } from "react-query";
import { deleteUser } from "@modules/Users/services/userCrud";

export default function UserList({
  users,
  setIsUpdate,
  setTypeDetail,
  handleShow,
  page,
  setPage,
  pageSize,
  setPageSize,
  search,
  setSearch,
  ordering,
  setOrdering,
  totalCount,
  isLoading,
}) {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Debounced search input
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput);
        setPage(1); // Reset to first page on search
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput, search, setSearch, setPage]);

  const handleEdit = (type) => {
    setTypeDetail(type);
    setIsUpdate(true);
    handleShow();
  };

  //query to delete user
  const { mutateAsync: userDelete } = useMutation(deleteUser, {
    onSuccess: () => {
      queryClient.invalidateQueries("users");
      queryClient.invalidateQueries("users-paginated");
      queryClient.invalidateQueries(["user", id]);
    },
  });

  const handlePageChange = React.useCallback(
    (newPage) => {
      setPage(newPage);
    },
    [setPage]
  );

  const handlePageSizeChange = React.useCallback(
    (newPageSize) => {
      setPageSize(newPageSize);
      setPage(1);
    },
    [setPageSize, setPage]
  );

  const handleSortChange = React.useCallback(
    (newOrdering) => {
      setOrdering(newOrdering);
      setPage(1); // Reset to first page on sort
    },
    [setOrdering, setPage]
  );

  const columns = useMemo(() => [
    {
      Header: `${t("username")}`,
      accessor: "username",
      Cell: (props) => {
        return (
          <>
            {props.row.original.first_name} {props.row.original.last_name}
          </>
        );
      },
    },
    {
      Header: `${t("email")}`,
      accessor: "email",
    },
    {
      Header: `${t("role")}`,
      accessor: "hardware_serial",
      Cell: (props) => {
        return (
          <span className="badge bg-primary user-role-badge" style={{ padding: "6px 10px" }}>
            {
              {
                1: `${t("admin")}`,
                2: `${t("user")}`,
              }[props.row.original.profile?.accountType]
            }
          </span>
        );
      },
    },
    {
      Header: `${t("actions")}`,
      accessor: "col4",
      style: { width: "80px", textAlign: "center" },
      disableSortBy: true,
      Cell: (props) => {
        return (
          <>
            <span title="Edit">
              <EditIcon
                onClick={() => {
                  handleEdit(props.row.original);
                }}
              />
            </span>
            <span title="Delete">
              <TrashIcon
                onClick={(e) => {
                  e.isPropagationStopped();
                  let id = props.row.original.id;
                  userDelete(id);
                }}
              />
            </span>
          </>
        );
      },
    },
  ]);

  //table row click event to get row data
  const onRowClicked = (row) => {
    const { id } = row.original;
    navigate(`/user/${id}`);
  };

  return (
    <>
      <ReactTable
        columns={columns}
        data={users}
        onRowClicked={onRowClicked}
        pagesize={pageSize}
        size={[10, 20, 30, 40, 50]}
        manualPagination
        pageCount={Math.ceil(totalCount / pageSize)}
        currentPage={page}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSortChange={handleSortChange}
        isLoading={isLoading}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
      />
    </>
  );
}
